import { useGetAllExamCentersQuery, useGetAllEnabledExamCoursesQuery } from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod'; // can't use shared/utils resolver, since it's not inherit output type
import {
  Box,
  Button,
  Divider,
  Flex,
  Form,
  FormDateInput,
  FormSearchMultipleSelectionWithSelectAllOption,
  FormSearchSelection,
  Text,
} from '@shared/components';
import { useMemo, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { QUESTIONNAIRE_TEMPLATE_OPTIONS } from '../../constants';
import { useQuestionnaireExportSearchParams } from '../../hooks';

import {
  type AdminQuestionnaireFilterFormInput,
  type AdminQuestionnaireFilterFormOutput,
  AdminQuestionnaireFilterSchema,
} from './schema';

export default function AdminQuestionnaireFilterSection() {
  const [
    { examCenters, startDate, endDate, questionnaireTemplate, examCourses },
    { changeFilter, resetAllParams },
  ] = useQuestionnaireExportSearchParams();

  const formContext = useForm<
    AdminQuestionnaireFilterFormInput,
    unknown,
    AdminQuestionnaireFilterFormOutput
  >({
    defaultValues: {
      examCenters: [],
      startDate: startDate ?? null,
      endDate: endDate ?? null,
      questionnaireTemplate: questionnaireTemplate
        ? {
            label:
              QUESTIONNAIRE_TEMPLATE_OPTIONS.find((o) => o.value === questionnaireTemplate)
                ?.label ?? questionnaireTemplate,
            value: questionnaireTemplate,
          }
        : null,
      examCourses: [],
    },
    resolver: zodResolver(AdminQuestionnaireFilterSchema),
  });
  const { setValue } = formContext;

  const { data: examCoursesData, loading: isLoadingExamCourses } =
    useGetAllEnabledExamCoursesQuery();
  const { data: examCenterData, loading: isLoadingExamCenters } = useGetAllExamCentersQuery();

  const watchedExamCenters = useWatch({ control: formContext.control, name: 'examCenters' });

  const examCenterOptions = useMemo(() => {
    return [...(examCenterData?.ecGetAllExamCenters?.examCenters ?? [])]
      .sort((a, b) => a.examCenterId - b.examCenterId)
      .map((ec) => ({ label: ec.code, value: ec.code }));
  }, [examCenterData]);

  const examCourseOptions = useMemo(() => {
    return [...(examCoursesData?.ecGetAllEnabledExamCourses?.examCourses ?? [])].map((c) => ({
      label: `${c.code} - ${c.name}`,
      value: c.code,
    }));
  }, [examCoursesData]);

  const handleSubmit = formContext.handleSubmit((data) => {
    changeFilter({
      examCenters: data.examCenters.map((c) => c.value),
      startDate: data.startDate,
      endDate: data.endDate,
      questionnaireTemplate: data.questionnaireTemplate.value,
      examCourses: data.examCourses.map((c) => c.value),
    });
  });

  const handleReset = () => {
    formContext.reset({
      examCenters: [],
      startDate: null,
      endDate: null,
      questionnaireTemplate: null,
      examCourses: [],
    });
    resetAllParams();
  };

  useEffect(() => {
    if (!examCenters || examCenters.length === 0 || examCenterOptions.length === 0) return;

    if (examCenters.includes('all')) {
      setValue('examCenters', [
        { label: `Select All (${examCenterOptions.length})`, value: 'all' },
      ]);
      return;
    }

    const matched = examCenters
      .map((code) => examCenterOptions.find((opt) => opt.value === code))
      .filter((opt): opt is NonNullable<typeof opt> => opt !== undefined);

    if (matched.length > 0) setValue('examCenters', matched);
  }, [examCenterOptions, examCenters, setValue]);

  useEffect(() => {
    if (examCourses.length === 0 || examCourseOptions.length === 0) return;

    if (examCourses.includes('all')) {
      setValue('examCourses', [
        { label: `Select All (${examCourseOptions.length})`, value: 'all' },
      ]);
      return;
    }

    const matched = examCourses
      .map((code) => examCourseOptions.find((opt) => opt.value === code))
      .filter((opt): opt is NonNullable<typeof opt> => opt !== undefined);

    if (matched.length > 0) setValue('examCourses', matched);
  }, [examCourseOptions, examCourses, setValue]);

  return (
    <FormProvider {...formContext}>
      <Form
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(4),
          p: theme.spacing(4),
          flexDirection: 'column',
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
        onSubmit={handleSubmit}
      >
        {/* Date Filter row */}
        <Flex sx={(theme) => ({ gap: theme.spacing(4), alignItems: 'flex-start' })}>
          <Text
            variant='body1Semibold'
            sx={(theme) => ({ minWidth: '120px', mt: theme.spacing(3.5) })}
          >
            Date Filter:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              alignItems: 'flex-start',
              flexWrap: 'nowrap',
            })}
          >
            <Box sx={{ width: '256px', flexShrink: 0 }}>
              <FormDateInput
                name='startDate'
                label='Start Date'
                placeholder='DD-MM-YYYY'
                inputFormat='DD-MM-YYYY'
                maxDate={new Date()}
              />
            </Box>
            <Text variant='body1' sx={(theme) => ({ mt: theme.spacing(8), flexShrink: 0 })}>
              to
            </Text>
            <Box sx={{ width: '256px', flexShrink: 0 }}>
              <FormDateInput
                name='endDate'
                label='End Date'
                placeholder='DD-MM-YYYY'
                inputFormat='DD-MM-YYYY'
                maxDate={new Date()}
              />
            </Box>
          </Flex>
        </Flex>

        {/* Other Filters row */}
        <Flex sx={(theme) => ({ gap: theme.spacing(4), alignItems: 'flex-start' })}>
          <Text
            variant='body1Semibold'
            sx={(theme) => ({ minWidth: '120px', mt: theme.spacing(3.5) })}
          >
            Other Filters:
          </Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4), flexWrap: 'wrap', flexGrow: 1 })}>
            <FormSearchMultipleSelectionWithSelectAllOption
              name='examCenters'
              label='Exam Center'
              options={examCenterOptions}
              loading={isLoadingExamCenters}
              placeholder='เลือกศูนย์ทดสอบ'
              slotProps={{
                selectAllOption: {
                  label: `Select All (${examCenterOptions.length})`,
                },
              }}
              multiple={true}
              sx={{ flexBasis: '256px', flexGrow: 1 }}
            />
            <FormSearchSelection
              name='questionnaireTemplate'
              label='Questionnaire Template'
              options={QUESTIONNAIRE_TEMPLATE_OPTIONS}
              placeholder='เลือก Template'
              sx={{ flexBasis: '256px', flexGrow: 1 }}
            />
            <FormSearchMultipleSelectionWithSelectAllOption
              name='examCourses'
              label='Exam Course'
              options={examCourseOptions}
              loading={isLoadingExamCourses}
              placeholder='เลือกหลักสูตร'
              disabled={!watchedExamCenters || watchedExamCenters.length === 0}
              slotProps={{
                selectAllOption: {
                  label: `Select All (${examCourseOptions.length})`,
                },
              }}
              multiple={true}
              sx={{ flexBasis: '256px', flexGrow: 1 }}
            />
          </Flex>
        </Flex>

        <Divider />
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button color='secondary' variant='outlined' onClick={handleReset}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
